import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { footerLinks } from '../data/mock';
import { BOT_GUIDES, ROLE_GUIDES, COMPARE_GUIDES } from '../data/guides';
import BusyBeeLogo from './BusyBeeLogo';
import LanguageSwitcher from './LanguageSwitcher';

// Columns of translated guide pages (labels from guide_links) and of the fixed
// links in data/mock.js (labels from footer.links).
const GUIDE_COLUMNS = [
  { id: 'bots', paths: ['/ai-bots', ...BOT_GUIDES.map((g) => g.path)] },
  { id: 'freelancers', paths: ['/use-cases', ...ROLE_GUIDES.map((g) => g.path)] },
  { id: 'compare', paths: [...COMPARE_GUIDES.map((g) => g.path), '/alternatives'] },
];
const COLUMNS = [
  { id: 'product',   key: 'products' },
  { id: 'pricing',   key: 'pricing' },
  { id: 'resources', key: 'resources' },
  { id: 'company',   key: 'company' },
  { id: 'legal',     key: 'legal' },
];
const Footer = () => {
  const { t } = useTranslation('landing');
  return (
    <footer className="bg-[#0a0a0b] border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8 mb-12">
          {GUIDE_COLUMNS.map((col) => (
            <div key={col.id}>
              <h4 className="text-white font-semibold mb-4">{t(`footer.columns.${col.id}`)}</h4>
              <ul className="space-y-2">
                {col.paths.map((path) => (
                  <li key={path}>
                    <Link to={path} className="text-gray-400 text-sm hover:text-white transition-colors">
                      {t(`guide_links.${path.slice(1)}.name`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {COLUMNS.map((col) => (
            <div key={col.id}>
              <h4 className="text-white font-semibold mb-4">{t(`footer.columns.${col.id}`)}</h4>
              <ul className="space-y-2">
                {footerLinks[col.key].map((link) => (
                  <li key={link.key}>
                    {/* The legal pages are written in English only and live at one address
                        (busybee.day/privacy, not /de/privacy): a plain link, not the router's,
                        which would put the language in front. */}
                    {col.key === 'legal' ? (
                      <a href={link.path} className="text-gray-400 text-sm hover:text-white transition-colors">
                        {t(`footer.links.${col.key}.${link.key}`)}
                      </a>
                    ) : (
                      <Link to={link.path} className="text-gray-400 text-sm hover:text-white transition-colors">
                        {t(`footer.links.${col.key}.${link.key}`)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom section */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center gap-6">
            <Link to="/" onClick={() => window.scrollTo({ top: 0 })}>
              <BusyBeeLogo className="h-8" />
            </Link>
            {/* BG_45: social icons removed - they pointed to generic
                twitter.com/linkedin.com/youtube.com homepages instead of
                official BusyBee.Day profiles (none exist yet). */}
          </div>

          {/* Language switcher + copyright.
              Operator identification (Act No. 22/2004 Coll. on electronic
              commerce, implementing Art. 5 of Directive 2000/31/EC) has to be
              easily and permanently accessible - it does not have to sit in the
              footer itself. The full company details live in Terms section 1
              and Privacy section 1, both linked from every page above, so the
              operator name here just makes it obvious who that is. */}
          <div className="flex flex-col md:flex-row items-center gap-4">
            <LanguageSwitcher variant="navbar" />
            <div className="text-gray-500 text-sm">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
